// Presence and people wording for People, Profile, Inbox and Invite, ported
// from the app (components/social/presence.dart, screens/inbox_screen.dart,
// models/activity.dart, screens/profile_page.dart). Pure functions, so the
// tests run them without a browser.
//
// One live window everywhere (SI-22): a heartbeat under 50 s is playing now.
import { LIVE_WINDOW_SECONDS, parseServerTime, rowTitle } from "./sessionRow.js";

export { parseServerTime };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Date.getDay(): Sunday first.
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const nowOf = (now) => (now instanceof Date ? now : new Date());
const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const daysBetween = (t, now) => Math.round((dayStart(now) - dayStart(t)) / 86400000);

// The server's placeholder bio is never shown (PersonDetail.bioOf).
const PLACEHOLDER_BIOS = new Set(["cloudloop user"]);

/** A heartbeat under the live window. */
export function isFresh(seen, now) {
  const t = parseServerTime(seen);
  if (!t) return false;
  return Math.abs(nowOf(now).getTime() - t.getTime()) < LIVE_WINDOW_SECONDS * 1000;
}

/**
 * The relative time on its own: "just now", "5m ago", "2h ago",
 * "yesterday", a weekday within the week ("Wed"), else "Oct 9" (with the
 * year when it isn't this year).
 */
export function when(value, now) {
  const t = parseServerTime(value);
  if (!t) return "";
  const n = nowOf(now);
  const mins = Math.floor((n - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const days = daysBetween(t, n);
  if (days <= 0) return `${Math.floor(mins / 60)}h ago`;
  if (days === 1) return "yesterday";
  if (days < 7) return WEEKDAYS[t.getDay()];
  const date = `${MONTHS[t.getMonth()]} ${t.getDate()}`;
  return t.getFullYear() === n.getFullYear() ? date : `${date}, ${t.getFullYear()}`;
}

/** "Last seen 5m ago". Empty when the time is unknown. */
export function lastSeenLabel(value, now) {
  const w = when(value, now);
  return w ? `Last seen ${w}` : "";
}

/**
 * Where someone is playing: "In Friday jam · 3 here", or "· alone" when
 * they play by themselves. Without `alone` (the MacPeople column) someone
 * by themselves reads just "In Basement tapes".
 */
export function playingIn(sessionTitle, here, { alone = true } = {}) {
  const n = Number(here) || 0;
  if (!alone && n <= 1) return `In ${sessionTitle}`;
  return `In ${sessionTitle} · ${n <= 1 ? "alone" : `${n} here`}`;
}

/** "now", "4 min", "1 h", "2 d"; with `suffix`: "just now", "4 min ago". */
export function shortAgo(value, now, { suffix = false } = {}) {
  const t = parseServerTime(value);
  if (!t) return "";
  const mins = Math.floor((nowOf(now) - t) / 60000);
  if (mins < 1) return suffix ? "just now" : "now";
  let s;
  if (mins < 60) s = `${mins} min`;
  else if (mins < 60 * 24) s = `${Math.floor(mins / 60)} h`;
  else s = `${Math.floor(mins / (60 * 24))} d`;
  return suffix ? `${s} ago` : s;
}

/** "Today", "Yesterday", a weekday within the week, else "Oct 9". */
export function dayAgo(value, now) {
  const t = parseServerTime(value);
  if (!t) return "";
  const days = daysBetween(t, nowOf(now));
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return WEEKDAYS[t.getDay()];
  return `${MONTHS[t.getMonth()]} ${t.getDate()}`;
}

/** "Oct 9" (the profile table's Updated column). */
export function monthDay(value) {
  const t = parseServerTime(value);
  return t ? `${MONTHS[t.getMonth()]} ${t.getDate()}` : "";
}

/** The name a person shows: their display name, else the username (d20). */
export function shownName(username, displayName) {
  const d = typeof displayName === "string" ? displayName.trim() : "";
  return d || String(username || "");
}

/** The first word of a shown name ("Maya" for "Maya R."). */
export function firstName(name) {
  const s = String(name || "").trim();
  return s.split(/\s+/)[0] || s;
}

/** A bio worth showing: never the server's "CloudLoop User" stand-in. */
export function bioOf(bio) {
  const s = typeof bio === "string" ? bio.trim() : "";
  if (!s || PLACEHOLDER_BIOS.has(s.toLowerCase())) return "";
  return s;
}

// A count the server sent, or null when it sent none (B4 is optional).
const countOf = (v) => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * A profile's three counts (profile_bloc.dart): the server's B4 counts
 * from GET /user?stats=1 when it sent them, else what the lists hold: the
 * sessions' total (or the rows loaded), the sound rows, and who they
 * follow (user.friends).
 */
export function profileStats(user, { sessionsTotal = null, sessions = [], sounds = [] } = {}) {
  const u = user && typeof user === "object" ? user : {};
  const friends = u.friends && typeof u.friends === "object" ? Object.keys(u.friends).length : 0;
  return {
    sessions: countOf(u.session_count) ?? countOf(sessionsTotal) ?? (sessions || []).length,
    sounds: countOf(u.sound_count) ?? (sounds || []).length,
    following: countOf(u.following_count) ?? friends,
  };
}

/**
 * "N sessions together" with someone else: B4's shared_session_count,
 * else /people/recent's, else their sessions you're in. 0 on your own
 * profile or signed out.
 */
export function togetherCount(user, { me = null, own = false, recentShared = null, sessions = [] } = {}) {
  if (own || !me) return 0;
  const u = user && typeof user === "object" ? user : {};
  const b4 = countOf(u.shared_session_count);
  if (b4 !== null) return b4;
  const recent = countOf(recentShared);
  if (recent !== null) return recent;
  return (sessions || []).filter((s) => s && (s.users || []).includes(me)).length;
}

/**
 * Where a profile's person plays now, for its Join: {sessionName, title}
 * or null. Only the server's live_session puts a Join up; it is null when
 * either of you blocked the other (S3), and a heartbeat in one of their
 * sessions never stands in for it. Their session list names it when it
 * holds it (the one with a fresh heartbeat first, as before).
 */
export function profileLive(user, sessions = [], { own = false, username = null, now } = {}) {
  if (own || !user) return null;
  const ls = user.live_session;
  if (!ls || typeof ls !== "object" || !ls.session_name) return null;
  const who = username || user.username;
  for (const s of sessions || []) {
    const seen = s && s.users_online && s.users_online[who];
    if (seen && isFresh(seen, now)) return { sessionName: s.name, title: rowTitle(s) };
  }
  const known = (sessions || []).find((s) => s && s.name === ls.session_name);
  return { sessionName: ls.session_name, title: known ? rowTitle(known) : ls.session_display_name || ls.session_name };
}

/**
 * A /friends?detail=1 or /search_users?detail=1 row, or a bare username,
 * as one shape: {username, displayName, picture, bio, lastSeenAt,
 * live: {sessionName, sessionTitle, here} | null, detailed}.
 */
export function personFromRow(row) {
  if (typeof row === "string") {
    return { username: row, displayName: null, picture: null, bio: "", lastSeenAt: null, live: null, detailed: false };
  }
  if (!row || typeof row !== "object" || !row.username) return null;
  const pic = row.picture_link || row.picture || "";
  const ls = row.live_session && typeof row.live_session === "object" ? row.live_session : null;
  return {
    username: String(row.username),
    displayName: typeof row.display_name === "string" && row.display_name.trim() ? row.display_name.trim() : null,
    picture: typeof pic === "string" && pic.startsWith("http") ? pic : null,
    bio: bioOf(row.bio),
    lastSeenAt: row.last_seen_at || null,
    live:
      ls && ls.session_name
        ? {
            sessionName: ls.session_name,
            sessionTitle: (ls.session_display_name && String(ls.session_display_name).trim()) || ls.session_name,
            here: Number(ls.online_count) || 0,
          }
        : null,
    sharedSessionCount: Number.isFinite(Number(row.shared_session_count)) ? Number(row.shared_session_count) : null,
    lastPlayedAt: row.last_played_at || null,
    lastSessionName: row.last_session_name || null,
    detailed: true,
  };
}

/**
 * An activity row's sentence (models/activity.dart): "Maya joined Friday
 * jam", "Theo saved a loop to Friday jam", "Jun saved 2 loops to …",
 * "Maya saved “bass” to …", "X accepted your invite to Y", "X declined
 * your invite to Y". Names are the display name's first word.
 */
export function activityText(row) {
  if (!row) return "";
  const who = firstName(shownName(row.actor_username, row.actor_display_name));
  const where = (row.session_display_name && String(row.session_display_name).trim()) || row.session_name || "";
  const detail = (row.detail && typeof row.detail === "object" && row.detail) || {};
  switch (row.kind) {
    case "joined":
      return `${who} joined ${where}`;
    case "loop_added": {
      const count = Number(detail.count);
      if (Number.isInteger(count) && count > 1) return `${who} saved ${count} loops to ${where}`;
      const name = typeof detail.loop_name === "string" ? detail.loop_name.trim() : "";
      return name ? `${who} saved “${name}” to ${where}` : `${who} saved a loop to ${where}`;
    }
    case "invite_accepted":
      return `${who} accepted your invite to ${where}`;
    case "invite_declined":
      return `${who} declined your invite to ${where}`;
    default:
      return "";
  }
}

export const ACTIVITY_KINDS = ["joined", "loop_added", "invite_accepted", "invite_declined"];

/**
 * An invitation card's meta line (the Inbox board): live, "Live now · 2
 * playing · 4 loops"; else "Invite only · 6 loops · 2 h ago".
 */
export function inviteMeta(inv, live, now) {
  const loops = Number(inv && inv.session_loop_count);
  const loopsText = Number.isFinite(loops) && loops > 0 ? `${loops} ${loops === 1 ? "loop" : "loops"}` : null;
  const parts = [];
  if (live) {
    parts.push("Live now");
    if (live.here > 0) parts.push(`${live.here} playing`);
  } else if (inv && inv.session_privacy === "invite") {
    parts.push("Invite only");
  }
  if (loopsText) parts.push(loopsText);
  if (!live) parts.push(shortAgo(inv && inv.created_at, now, { suffix: true }));
  return parts.filter(Boolean).join(" · ");
}

/**
 * One of your sessions in the Invite to… menu: "3 loops · you, Jun ·
 * yesterday", or "2 loops · just you · Wed".
 */
export function inviteToLine(session, me, now, nameOf = (u) => u) {
  const loops = Number(session && (session.library_count ?? (Array.isArray(session.library) ? session.library.length : 0))) || 0;
  const others = [];
  for (const u of [session && session.creator, ...((session && session.users) || [])]) {
    if (u && u !== me && !others.includes(u)) others.push(u);
  }
  const who = others.length ? ["you", ...others.map(nameOf)].join(", ") : "just you";
  return [`${loops} ${loops === 1 ? "loop" : "loops"}`, who, when(session && session.created_at, now)].filter(Boolean).join(" · ");
}

// An email as typed in the Invite search.
export const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const isEmail = (s) => EMAIL.test(String(s || "").trim());
